import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import {
  collection,
  query,
  onSnapshot,
} from 'firebase/firestore';
import UserManagement from './SuperAdmin/UserManagement';
import ReportsComplaints from './SuperAdmin/ReportsComplaints';
import Analytics from './SuperAdmin/Analytics';
import SystemSettings from './SuperAdmin/SystemSettings';

function SuperAdminDashboard() {
  const { db, userProfile, isAuthReady } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    freeUsers: 0,
    premiumUsers: 0,
    totalReports: 0,
    todayRegistrations: 0,
    totalMatches: 0
  });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);

  // Fetch dashboard statistics
  useEffect(() => {
    if (!isAuthReady || userProfile?.role !== 'super_admin') return;

    const fetchStats = async () => {
      try {
        const usersRef = collection(db, 'users');
        const reportsRef = collection(db, 'reports');
        const interestsRef = collection(db, 'interests');

        // Get total users
        const totalUsersUnsubscribe = onSnapshot(query(usersRef), (snapshot) => {
          const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setUsers(usersData);
          
          const totalUsers = usersData.length;
          const activeUsers = usersData.filter(user => 
            !user.isDeactivated && user.isAccountActive !== false
          ).length;
          
          const freeUsers = usersData.filter(user => 
            (!user.membershipType || user.membershipType === 'free') && 
            (!user.isDeactivated && user.isAccountActive !== false)
          ).length;
          
          const premiumUsers = usersData.filter(user => 
            user.membershipType === 'premium' && 
            (!user.isDeactivated && user.isAccountActive !== false)
          ).length;
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayRegistrations = usersData.filter(user => {
            const createdAt = user.createdAt?.toDate?.();
            return createdAt && createdAt >= today;
          }).length;

          setDashboardStats(prev => ({
            ...prev,
            totalUsers,
            activeUsers,
            freeUsers,
            premiumUsers,
            todayRegistrations
          }));
        });

        // Get total reports
        const reportsUnsubscribe = onSnapshot(query(reportsRef), (snapshot) => {
          const reportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setReports(reportsData);
          
          setDashboardStats(prev => ({
            ...prev,
            totalReports: snapshot.size
          }));
        });

        // Get total matches
        const matchesUnsubscribe = onSnapshot(query(interestsRef), (snapshot) => {
          const interests = snapshot.docs.map(doc => doc.data());
          
          const mutualMatches = new Set();
          interests.forEach(interest => {
            const reverseInterest = interests.find(
              int => int.senderId === interest.receiverId && 
                     int.receiverId === interest.senderId
            );
            if (reverseInterest) {
              const matchKey = [interest.senderId, interest.receiverId].sort().join('-');
              mutualMatches.add(matchKey);
            }
          });
          
          setDashboardStats(prev => ({
            ...prev,
            totalMatches: mutualMatches.size
          }));
        });

        setLoading(false);

        return () => {
          totalUsersUnsubscribe();
          reportsUnsubscribe();
          matchesUnsubscribe();
        };
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setLoading(false);
      }
    };

    fetchStats();
  }, [db, isAuthReady, userProfile]);

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white p-8 rounded-2xl shadow-xl">
        <h2 className="text-3xl font-bold mb-2 text-slate-800">Super Admin Dashboard</h2>
        <p className="text-gray-600">Welcome back! Here's an overview of your matrimony platform.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Total Users</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.totalUsers}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-yellow-100">
              👥
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Active Users</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.activeUsers}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-green-100">
              ✅
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Free Users</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.freeUsers}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-blue-100">
              👤
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Premium Users</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.premiumUsers}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-purple-100">
              👑
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Reports</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.totalReports}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-red-100">
              ⚠️
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Today's Registrations</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.todayRegistrations}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-indigo-100">
              📅
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 md:col-span-2 lg:col-span-1">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Total Matches</p>
              <p className="text-4xl font-bold text-slate-800">{dashboardStats.totalMatches}</p>
            </div>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-pink-100">
              💕
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-8 rounded-2xl shadow-xl">
        <h3 className="text-2xl font-bold mb-6 text-slate-800">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <button
            onClick={() => setActiveTab('users')}
            className="p-6 bg-white border-2 border-gray-200 hover:border-yellow-500 rounded-2xl text-left transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="text-3xl mb-3">👤</div>
            <h4 className="font-bold text-lg mb-2 text-slate-800">Manage Users</h4>
            <p className="text-sm text-gray-600">View and manage user accounts</p>
          </button>

          <button
            onClick={() => setActiveTab('reports')}
            className="p-6 bg-white border-2 border-gray-200 hover:border-yellow-500 rounded-2xl text-left transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="text-3xl mb-3">⚠️</div>
            <h4 className="font-bold text-lg mb-2 text-slate-800">Handle Reports</h4>
            <p className="text-sm text-gray-600">Review user complaints</p>
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className="p-6 bg-white border-2 border-gray-200 hover:border-yellow-500 rounded-2xl text-left transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="text-3xl mb-3">📈</div>
            <h4 className="font-bold text-lg mb-2 text-slate-800">View Analytics</h4>
            <p className="text-sm text-gray-600">Check platform statistics</p>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className="p-6 bg-white border-2 border-gray-200 hover:border-yellow-500 rounded-2xl text-left transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
          >
            <div className="text-3xl mb-3">⚙️</div>
            <h4 className="font-bold text-lg mb-2 text-slate-800">System Settings</h4>
            <p className="text-sm text-gray-600">Configure platform settings</p>
          </button>
        </div>
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement users={users} />;
      case 'reports':
        return <ReportsComplaints reports={reports} />;
      case 'analytics':
        return <Analytics dashboardStats={dashboardStats} />;
      case 'settings':
        return <SystemSettings />;
      default:
        return renderDashboard();
    }
  };

  if (!isAuthReady || userProfile?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="text-center">
          <p className="text-2xl text-white mb-2">Access Denied</p>
          <p className="text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-xl text-white">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    { key: 'users', label: 'User Management', icon: '👥' },
    { key: 'reports', label: 'Reports & Complaints', icon: '⚠️' },
    { key: 'analytics', label: 'Analytics', icon: '📈' },
    { key: 'settings', label: 'System Settings', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md border-b-2 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-slate-800">Super Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Logged in as: <span className="font-bold text-slate-800">{userProfile?.firstName || userProfile?.email}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm border-b-2 border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-2 border-b-4 font-semibold text-sm whitespace-nowrap transition-all duration-300 ${
                  activeTab === tab.key
                    ? 'border-yellow-500 text-yellow-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderActiveTab()}
      </div>
    </div>
  );
}

export default SuperAdminDashboard;